import EmojiPicker, {EmojiClickData, SuggestionMode} from 'emoji-picker-react';
import {Activity, Palette} from 'hubbl-shared';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {TextBodyMedium, TextBodySmall, TextHeading6} from '../index';
import {RadioButtonCircle} from '../pill/components/radio-button-circle/RadioButtonCircle';
import {useAppSelector, useI18n} from '../../hooks';
import {GlobalSelector} from '../../store/global/GlobalSelector';

interface ActivitySelectorProps {
  activities: Activity[];
  selectedActivities: Activity[];
  onActivityToggle: (activity: Activity) => void;
  onCustomActivityChange: (activity: string) => void;
  onCustomEmojiChange: (emoji: string) => void;
  customActivity: string;
  customEmoji: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const ActivityCard = styled.div<{selected: boolean}>`
  border: 2px solid
    ${props => (props.selected ? Palette.primaryTerraCotta : '#e0e0e0')};
  background: ${props => (props.selected ? '#fff5f5' : 'white')};
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${Palette.primaryTerraCotta};
    background: #fff5f5;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const RadioButtonContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const ActivityIcon = styled.div`
  margin-bottom: 8px;
  font-size: 2.5rem;
  line-height: 1;
`;

const CustomActivitySection = styled.div<{selected: boolean}>`
  margin-top: 16px;
  padding: 16px;
  background: ${props =>
    props.selected ? '#fff5f5' : Palette.primarySubtleGreen};
  border-radius: 12px;
  border: 2px solid
    ${props =>
      props.selected
        ? Palette.primaryTerraCotta
        : Palette.almostBlackTransparent10};
  position: relative;
  transition: all 0.2s ease;
`;

const CustomRadioButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const CustomActivityHeader = styled.div`
  margin-bottom: 12px;
`;

const CustomActivityInput = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  background: white;
  border: 1px solid ${Palette.almostBlackTransparent10};
  border-radius: 8px;
  padding: 8px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${Palette.primaryTerraCotta};
    box-shadow: 0 0 0 2px rgba(224, 71, 48, 0.1);
  }
`;

const EmojiButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${Palette.primaryCherokee};
    transform: scale(1.05);
  }
`;

const InputContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${Palette.almostBlack};
  padding: 8px 0;

  &::placeholder {
    color: ${Palette.almostBlack500};
  }
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: ${Palette.almostBlack500};
  margin-left: 8px;
  flex-shrink: 0;
`;

const EmojiPickerContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  margin-top: 8px;
  border-radius: 16px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.08);
  background: white;

  .EmojiPickerReact {
    --epr-hover-bg-color: ${Palette.primaryCherokee};
    --epr-bg-color: ${Palette.white};
    --epr-category-label-bg-color: ${Palette.white};
    --epr-text-color: ${Palette.almostBlack};
    --epr-highlight-color: ${Palette.primaryCherokee};
    --epr-search-input-bg-color: ${Palette.primarySubtleGreen};
    --epr-search-input-bg-color-active: ${Palette.primarySubtleGreen};
  }
`;

const CustomActivityInputContainer = styled.div`
  position: relative;
`;

export const ActivitySelector = memo(
  ({
    activities,
    selectedActivities,
    onActivityToggle,
    onCustomActivityChange,
    onCustomEmojiChange,
    customActivity,
    customEmoji,
  }: ActivitySelectorProps) => {
    const t = useI18n();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement>(null);

    const prefLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    const handleEmojiSelect = useCallback(
      (emoji: string) => {
        onCustomEmojiChange(emoji);
        setShowEmojiPicker(false);
      },
      [onCustomEmojiChange],
    );

    const handleEmojiClick = useCallback(
      (emojiData: EmojiClickData) => {
        handleEmojiSelect(emojiData.emoji);
      },
      [handleEmojiSelect],
    );

    // Close emoji picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          emojiPickerRef.current &&
          !emojiPickerRef.current.contains(event.target as Node)
        ) {
          setShowEmojiPicker(false);
        }
      };

      if (showEmojiPicker) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showEmojiPicker]);

    return (
      <Container>
        <TextHeading6 text={t.events.selectActivity} />
        <TextBodyMedium
          text={t.events.chooseActivityOrCreate}
          color={Palette.almostBlack500}
        />

        <ActivitiesGrid>
          {activities.map(activity => (
            <ActivityCard
              key={activity.id}
              selected={selectedActivities.some(a => a.id === activity.id)}
              onClick={() => onActivityToggle(activity)}>
              <RadioButtonContainer>
                <RadioButtonCircle
                  isSelected={selectedActivities.some(
                    a => a.id === activity.id,
                  )}
                />
              </RadioButtonContainer>
              <ActivityIcon>
                <span>{activity.emoji}</span>
              </ActivityIcon>
              <TextBodySmall
                text={activity.label[prefLanguage]}
                color={Palette.almostBlack}
              />
            </ActivityCard>
          ))}
        </ActivitiesGrid>

        <CustomActivitySection
          selected={selectedActivities.some(a => a.id === 'custom')}
          onClick={() => {
            // Toggle custom activity selection
            const isCustomSelected = selectedActivities.some(
              a => a.id === 'custom',
            );
            if (isCustomSelected) {
              // If custom is selected, deselect it

              onActivityToggle({
                id: 'custom',
                label: {en: '', da: ''},
                emoji: '',
                category: 'break',
                color: Palette.primaryPadua,
              } as Activity);
            } else {
              // If custom is not selected, select it (if there's text)
              if (customActivity.trim()) {
                const customActivityObj: Activity = {
                  id: 'custom',
                  label: {en: customActivity, da: customActivity},
                  emoji: customEmoji,
                  category: 'break',
                  color: Palette.primaryPadua,
                };
                onActivityToggle(customActivityObj);
              }
            }
          }}>
          <CustomRadioButtonContainer>
            <RadioButtonCircle
              isSelected={selectedActivities.some(a => a.id === 'custom')}
            />
          </CustomRadioButtonContainer>
          <CustomActivityHeader>
            <TextHeading6 text={t.events.orCreateYourOwn} />
          </CustomActivityHeader>

          <CustomActivityInputContainer>
            <CustomActivityInput>
              <EmojiButton
                type="button"
                onClick={() => setShowEmojiPicker(true)}>
                <span style={{fontSize: '20px'}}>{customEmoji || '😊'}</span>
              </EmojiButton>

              <InputContainer>
                <StyledInput
                  value={customActivity}
                  onChange={e => onCustomActivityChange(e.target.value)}
                  placeholder={t.events.writeCustomActivity}
                  maxLength={30}
                />
                <CharacterCount>{customActivity.length}/30</CharacterCount>
              </InputContainer>
            </CustomActivityInput>

            {showEmojiPicker && (
              <EmojiPickerContainer ref={emojiPickerRef}>
                <EmojiPicker
                  suggestedEmojisMode={SuggestionMode.RECENT}
                  onEmojiClick={handleEmojiClick}
                />
              </EmojiPickerContainer>
            )}
          </CustomActivityInputContainer>
        </CustomActivitySection>
      </Container>
    );
  },
);
